import { useMemo } from 'react';
import type { HistoryResponse, LeagueBundle } from '../../shared/types';
import {
  Avatar,
  Empty,
  ErrorState,
  Legend,
  Loading,
  PageHead,
  Section,
  Stat,
} from '../components/common';
import { useSchedule, useScores } from '../lib/api';
import { fmtOdds, fmtPoints, ordinal } from '../lib/format';
import { buildTeamViews, teamIndex } from '../lib/league';
import { buildStrength, simulateSeason, type OddsRow } from '../lib/odds';
import { Link } from '../lib/router';

/** A team's seed distribution as a single bar, playoff seats tinted. */
function SeedBar({ row, playoffTeams }: { row: OddsRow; playoffTeams: number }) {
  return (
    <div className="seedbar" aria-hidden="true">
      {row.seedShare.map((share, i) => (
        <span
          key={i}
          className={i < playoffTeams ? 'seedbar-in' : 'seedbar-out'}
          style={{ flexGrow: Math.max(share, 0.001) }}
          title={`${ordinal(i + 1)}: ${fmtOdds(share)}`}
        />
      ))}
    </div>
  );
}

function OddsCell({ value, settled }: { value: number; settled?: 'in' | 'out' | null }) {
  if (settled === 'in') return <span className="pill pill-good">In</span>;
  if (settled === 'out') return <span className="pill pill-bad">Out</span>;
  return <span className="tabnum">{fmtOdds(value)}</span>;
}

export default function Playoffs({
  bundle,
  history,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
}) {
  const schedule = useSchedule(bundle.league.league_id);

  // Completed seasons only. The season in progress is already the subject, and
  // feeding it in as its own prior would count every game twice.
  const priorLeagueIds = useMemo(
    () =>
      (history?.seasons ?? [])
        .filter((s) => s.season !== bundle.league.season)
        .sort((a, b) => Number(a.season) - Number(b.season))
        .map((s) => s.league_id),
    [history, bundle.league.season],
  );
  const priors = useScores(priorLeagueIds);

  const teams = useMemo(() => buildTeamViews(bundle), [bundle]);
  const byRoster = useMemo(() => teamIndex(teams), [teams]);

  const model = useMemo(
    () => buildStrength(schedule.data, priors.data ?? []),
    [schedule.data, priors.data],
  );

  const sim = useMemo(
    () => simulateSeason(schedule.data, bundle.league.playoff_teams, model),
    [schedule.data, bundle.league.playoff_teams, model],
  );

  if (schedule.loading) return <Loading label="Loading the fixture list" />;
  if (schedule.error || !schedule.data) {
    return <ErrorState message={schedule.error ?? 'No schedule returned'} onRetry={schedule.reload} />;
  }
  if (schedule.data.games.length === 0) {
    return (
      <>
        <PageHead title="Playoff Odds" meta={[`${schedule.data.season} season`]} />
        <div className="card">
          <Empty>Sleeper has not generated a schedule for this season yet.</Empty>
        </div>
      </>
    );
  }

  const name = (id: number) => byRoster.get(id)?.name ?? `Roster ${id}`;
  const avatar = (id: number) => byRoster.get(id)?.avatar ?? null;

  const basis =
    model.basis === 'season'
      ? `${sim.gamesPlayed} games this season`
      : model.basis === 'prior'
        ? 'last season, pending a game this year'
        : 'league defaults';

  return (
    <>
      <PageHead
        title="Playoff Odds"
        meta={[
          `${schedule.data.season} season`,
          sim.gamesPlayed === 0
            ? `${schedule.data.games.length} games to play`
            : sim.gamesRemaining === 0
              ? `All ${schedule.data.games.length} games played`
              : `${sim.gamesRemaining} of ${schedule.data.games.length} left`,
          `Top ${sim.playoffTeams} qualify`,
          `${sim.sims.toLocaleString('en-US')} simulated seasons`,
        ]}
      />

      <Section title="How the seasons are simulated">
        <div className="grid grid-4">
          <Stat
            label="Scoring level"
            value={fmtPoints(model.leagueMean)}
            note="league average per team per week"
          />
          <Stat
            label="Week-to-week swing"
            value={`±${model.sigma.toFixed(1)}`}
            note={`measured from ${basis}`}
          />
          <Stat
            label="Unknown per team"
            value={`±${(model.teams.get(teams[0]?.roster_id)?.uncertainty ?? 0).toFixed(1)}`}
            note="how wrong a strength estimate could be"
          />
          <Stat
            label="Settles after"
            value={`${model.shrinkage.toFixed(1)} games`}
            note="when a record outweighs the preseason view"
          />
        </div>
      </Section>

      <Section
        title="Odds"
        hint={sim.gamesRemaining === 0 ? 'Season complete' : `${sim.gamesRemaining} games to play`}
      >
        <div className="card table-scroll">
          <table className="picks-table">
            <thead>
              <tr>
                <th>Team</th>
                <th className="num">Strength</th>
                <th className="num">Proj. W</th>
                <th className="num">Playoffs</th>
                <th className="num">Bye</th>
                <th className="num">Title</th>
                <th className="num">Seed</th>
                <th>Where they finish</th>
              </tr>
            </thead>
            <tbody>
              {sim.rows.map((row) => {
                const strength = model.teams.get(row.rosterId);
                return (
                  <tr key={row.rosterId}>
                    <td>
                      <Link to={`/teams/${row.rosterId}`}>
                        <div className="team-cell">
                          <Avatar id={avatar(row.rosterId)} alt={name(row.rosterId)} />
                          <div className="team-names">
                            <div className="team-title">{name(row.rosterId)}</div>
                            <div className="team-handle">
                              {strength && strength.games > 0
                                ? `${strength.games} played`
                                : 'no games yet'}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="num tabnum">{fmtPoints(strength?.strength ?? 0)}</td>
                    <td className="num tabnum">{row.meanWins.toFixed(1)}</td>
                    <td className="num">
                      <OddsCell value={row.playoff} settled={row.clinch} />
                    </td>
                    <td className="num tabnum faint">{fmtOdds(row.bye)}</td>
                    <td className="num tabnum">{fmtOdds(row.title)}</td>
                    <td className="num tabnum faint">{row.meanSeed.toFixed(1)}</td>
                    <td className="seedbar-cell">
                      <SeedBar row={row} playoffTeams={sim.playoffTeams} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Legend
          items={[
            { term: 'Strength', def: 'expected points per week, after shrinking toward the league' },
            { term: 'Proj. W', def: 'mean wins across every simulated season' },
            { term: 'Bye', def: `top ${sim.byes} seeds skip the first round` },
            { term: 'Seed', def: 'mean finishing position' },
            {
              term: 'Where they finish',
              def: `share of seasons at each seat, first at the left; the first ${sim.playoffTeams} are the field`,
            },
          ]}
        />
      </Section>

      {sim.swings.length > 0 && sim.swingWeek !== null && (
        <Section
          title={`Week ${sim.swingWeek}`}
          hint="Playoff odds after this game, either way"
        >
          <div className="card">
            {sim.swings.map((s) => (
              <div className="swing" key={s.matchupId}>
                <div className="swing-head">
                  <span className="swing-swing tabnum">
                    {(s.swing * 100).toFixed(0)}
                    <span className="faint"> pp</span>
                  </span>
                  <span className="hint">biggest move</span>
                </div>
                <div className="swing-sides">
                  {[
                    { id: s.rosterA, win: s.aIfWin, lose: s.aIfLose },
                    { id: s.rosterB, win: s.bIfWin, lose: s.bIfLose },
                  ].map((side) => (
                    <div className="swing-side" key={side.id}>
                      <div className="team-cell">
                        <Avatar id={avatar(side.id)} alt={name(side.id)} />
                        <div className="team-names">
                          <div className="team-title">{name(side.id)}</div>
                        </div>
                      </div>
                      <div className="swing-odds">
                        <span className="swing-win tabnum">{fmtOdds(side.win)}</span>
                        <span className="faint">win</span>
                        <span className="swing-lose tabnum">{fmtOdds(side.lose)}</span>
                        <span className="faint">lose</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Legend
            items={[
              {
                term: 'Either way',
                def: 'playoff odds recomputed with the result already known',
              },
              {
                term: 'Why winning moves it so far',
                def: 'a result is both a win in the table and evidence about the roster',
              },
            ]}
          />
        </Section>
      )}

      <Section title="Method">
        <Legend
          items={[
            {
              term: 'Strength',
              def: `each team's scoring average pulled toward the league's, by an amount set from how much of the spread is real`,
            },
            {
              term: 'Uncertainty',
              def: 'strength is drawn afresh each simulated season, so a wrong estimate costs a whole year, not one week',
            },
            {
              term: 'Carryover',
              def: model.carryoverMeasured
                ? `${Math.round(model.carryover * 100)}% of last season's edge, measured across ${priorLeagueIds.length} seasons`
                : `${Math.round(model.carryover * 100)}% of last season's edge, assumed until enough seasons exist to measure it`,
            },
            { term: 'Seeding', def: 'record first, then points for, as Sleeper ranks it' },
            {
              term: 'Bracket',
              def: `fixed, not reseeded: the top ${sim.byes} rest, then ${sim.playoffTeams >= 6 ? 'the 1 seed meets the 4/5 winner' : 'the highest seed meets the lowest survivor'}`,
            },
          ]}
        />
      </Section>
    </>
  );
}
